import * as Sentry from '@sentry/browser';
import { getStepPayload, stepPayloadDefaults } from './tealiumStepPayloadMap';

const MODULE_NAME_MAX_MORTGAGE = 'maximaleHypotheek';
const MODULE_NAME_MONTHLY_CHARGE = 'maandlasten';
const MODULE_NAME_REFINANCE_RATE_AVERAGING = 'oversluitenRentemiddelen';

// set upon initTealiumListeners
let loggerLevelLocal;

export const TEALIUM_EVENTS = [
  'APPOINTMENT_CREATED',
  'APPOINTMENT_APP_SEGMENTATION_CHANGED',
  'CTA_SAVE_DOSSIER_STARTED',
  'CTA_SAVE_DOSSIER_ERROR',
  'CTA_SAVE_DOSSIER_SUCCESS',
  'INTEREST_COMPARE_CALCULATION_TRIGGERED',
  'INTEREST_COMPARE_RESULTS_SORTED',
  'INTEREST_COMPARE_ITEM_EXPANDED',
  'INTEREST_COMPARE_LOADED',
  'TOOL_STEP_CHANGED',
  'TOOL_STEP_LOADED',
];

function getTealiumToolName(storeNamespace, toolKind) {
  if (storeNamespace === MODULE_NAME_MAX_MORTGAGE)
    return 'Maximaal lenen';

  if (storeNamespace === MODULE_NAME_MONTHLY_CHARGE)
    return 'Maandlasten';

  if (storeNamespace === MODULE_NAME_REFINANCE_RATE_AVERAGING) {
    if (toolKind === 'rentemiddelen')
      return 'Rentemiddelen';

    return 'Oversluiten';
  }

  return 'Onbekend';
}

function getFormStepIncrement(storeNamespace) {
  if (storeNamespace === MODULE_NAME_REFINANCE_RATE_AVERAGING)
    return 4;

  return 0;
}

/**
 * Pushes event to tealium datalayer
 *
 * @param {String} eventType. Event name
 * @param {Object} payload. Payload passed
 * @return void
 */
function pushTealiumDatalayer(eventType, payload = undefined) {
  if (loggerLevelLocal === 'info')
    console.info('[trackTealium]', { eventType, payload }); // eslint-disable-line no-console

  const event = [eventType];

  if (payload)
    event.push(payload);

  global.adv_event.push(event);

  return global.adv_event;
}

/**
 * Appointment created tracking resolver
 *
 * @return void
 */
function resolveAppointmentCreated() {
  return pushTealiumDatalayer('hypotheekadvies afspraak maken end');
}

/**
 * Appointment app loaded tracking resolver
 *
 * @return void
 */
function resolveAppointmentAppStarted() {
  return pushTealiumDatalayer('hypotheekadvies afspraak maken start');
}

/**
 * Calculation tool started & tool step loaded
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveToolStepLoaded({ payload }) {
  const stepPayload = getStepPayload(payload);

  if (!stepPayload) {
    // eslint-disable-next-line no-console
    console.log(`[trackTealium.js] Skipping... no step payload found (tool: ${payload.toolName}, step: ${payload.step}, to: ${payload.to})`);
    return;
  }

  return pushTealiumDatalayer('pageview', stepPayload);
}

/**
 * Calculation tool finished
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveInterestCompareCalculationTriggered() {
  return pushTealiumDatalayer('rentevergelijker berekenen');
}

/**
 * Calculation tool finished
 *
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveInterestCompareResultsSorted({ payload }) {
  return pushTealiumDatalayer('rentevergelijker sort results', payload);
}

/**
 * Cta save dossier interaction
 *
 * @param {String} event. Event name
 * @param {Object} payload. Payload passed
 * @return void
 */
function resolveCtaSaveDossierStarted({ payload }) {
  const formStepIncrement = getFormStepIncrement(payload.storeNamespace);
  const tealiumToolName = getTealiumToolName(payload.storeNamespace, payload.toolKind);
  const tealiumPayload = {
    page_name: `Hypotheek:Advies:${tealiumToolName}:Bewaar mijn berekening`,
    form_name: tealiumToolName,
    form_step: (formStepIncrement + 3).toString(),
    form_step_name: 'Bewaar mijn berekening',
    form_status: [],
  };

  return pushTealiumDatalayer('pageview', { ...tealiumPayload, ...stepPayloadDefaults });
}

function resolveCtaSaveDossierError({ payload }) {
  const formStepIncrement = getFormStepIncrement(payload.storeNamespace);
  const tealiumToolName = getTealiumToolName(payload.storeNamespace, payload.toolKind);
  const tealiumPayload = {
    page_name: `Hypotheek:Advies:${tealiumToolName}:Dit e-mailadres is al bekend bij ons`,
    form_name: tealiumToolName,
    form_step: `${(formStepIncrement + 3).toString()}a`,
    form_step_name: 'Dit e-mailadres is al bekend bij ons',
    form_status: ['end', 'failure'],
  };

  return pushTealiumDatalayer('pageview', { ...tealiumPayload, ...stepPayloadDefaults });
}

function resolveCtaSaveDossierSuccess({ payload }) {
  const formStepIncrement = getFormStepIncrement(payload.storeNamespace);
  const tealiumToolName = getTealiumToolName(payload.storeNamespace, payload.toolKind);
  const tealiumPayload = {
    page_name: `Hypotheek:Advies:${tealiumToolName}:Dossier aangemaakt`,
    form_name: tealiumToolName,
    form_step: (formStepIncrement + 4).toString(),
    form_step_name: 'Dossier aangemaakt',
    form_status: ['end', 'success'],
    tips_email_optin: payload.submittedForm.subscribe_personal || false,
  };

  return pushTealiumDatalayer('pageview', { ...tealiumPayload, ...stepPayloadDefaults });
}

/**
 * Interest compare item expanded
 *
 * @return void
 */
function resolveInterestCompareItemExpanded() {
  return pushTealiumDatalayer('pageview', {
    page_name: 'Hypotheek:Vergelijken:Meer informatie',
    page_type: 'Vergelijken',
    compare_type: 'Hypotheek vergelijker',
    form_name : 'Hypotheek vergelijker',
    form_step: '1a',
    form_step_name: 'Meer informatie',
    form_type: 'Vergelijker',
    form_status: [],
  });
}

/**
 * Interest compare app loaded
 *
 * @return void
 */
function resolveInterestCompareLoaded() {
  return pushTealiumDatalayer('pageview', {
    page_name: 'Hypotheek:Vergelijken:Resultaten',
    page_type: 'Vergelijken',
    compare_type: 'Hypotheek vergelijker',
    form_name : 'Hypotheek vergelijker',
    form_step: '1',
    form_step_name: 'Resultaten',
    form_type: 'Vergelijker',
    form_status: ['start'],
  });
}

const eventResolvers = {
  APPOINTMENT_CREATED: resolveAppointmentCreated,
  APPOINTMENT_APP_SEGMENTATION_CHANGED: resolveAppointmentAppStarted,

  CTA_SAVE_DOSSIER_STARTED: resolveCtaSaveDossierStarted,
  CTA_SAVE_DOSSIER_ERROR: resolveCtaSaveDossierError,
  CTA_SAVE_DOSSIER_SUCCESS: resolveCtaSaveDossierSuccess,

  INTEREST_COMPARE_CALCULATION_TRIGGERED: resolveInterestCompareCalculationTriggered,
  INTEREST_COMPARE_RESULTS_SORTED: resolveInterestCompareResultsSorted,
  INTEREST_COMPARE_ITEM_EXPANDED: resolveInterestCompareItemExpanded,
  INTEREST_COMPARE_LOADED: resolveInterestCompareLoaded,
  TOOL_STEP_CHANGED: resolveToolStepLoaded,
  TOOL_STEP_LOADED: resolveToolStepLoaded,
};

/**
 * Entry function tealium tracking
 *
 * Checks if a specific resolver exists for an event and executes,
 * and otherwise throws an error
 *
 * @param {String} event. Event name
 * @param {Object} payload. Payload passed
 * @return void
 */
async function trackTealium(event, payload = {}) {
  try {
    if (!eventResolvers[event])
      throw new Error(`[trackTealium.js] No resolver found for tealium event: ${event}`);

    eventResolvers[event]({ event, payload: payload.data });

  } catch (err) {
    Sentry.captureException(err);
  }
}

export function initTealiumListeners({ $bus, loggerLevel }) {
  loggerLevelLocal = loggerLevel;
  global.adv_event = global.adv_event || [];

  TEALIUM_EVENTS.forEach((event) => {
    $bus[event].listen((payload) => {
      return trackTealium(event, payload);
    });
  });
}
